/**
 * Display gate for promotion popups, based on two backend fields:
 *
 *  - targetAudience (Promotion_popup.target_audience):
 *      "inactive" (default/legacy): visitors + logged-in non-active users
 *      "active": only logged-in active subscribers
 *      "all": everyone
 *
 *  - targetPlanTypes (Promotion_popup.target_plan_types): list of PlanType ids
 *    (1 = Basic, 2 = Plus, 3 = Gold). It only narrows the "active" audience;
 *    "inactive" and "all" ignore it. Missing/empty, or holding every plan
 *    type, means "no restriction".
 *
 * Both fall back to the historical behavior when absent, so API payloads
 * cached before this feature (the endpoint caches for a year) keep working.
 */
type PromotionAudienceInput = {
  targetAudience?: string;
  targetPlanTypes?: number[] | null;
};

const ALL_PLAN_TYPE_IDS_COUNT = 3;

export function usePromotionAudience() {
  function canSeePromotion(promotion?: PromotionAudienceInput | null): boolean {
    const storeUser = userStore();
    const audience = promotion?.targetAudience ?? "inactive";

    if (audience === "all") return true;
    if (audience !== "active") return !storeUser.isActiveUser;
    if (!storeUser.isActiveUser) return false;

    const allowed = Array.isArray(promotion?.targetPlanTypes)
      ? promotion!.targetPlanTypes!.map(Number)
      : [];

    // No restriction: never look at the plan. Failing open matters here —
    // admins count as active but have no plan, and the persisted plan id can
    // still be stale on the first paint after boot.
    if (allowed.length === 0 || allowed.length >= ALL_PLAN_TYPE_IDS_COUNT) {
      return true;
    }

    const planTypeId = storeUser.planTypeId;
    return planTypeId !== null && allowed.includes(Number(planTypeId));
  }

  return { canSeePromotion };
}
